import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { StoreManagerService } from "../../store-manager/services/store-manager.service";

interface CrowdinManifest {
  languages: string[];
  custom_languages: { [key: string]: { locale: string } };
  content: { [key: string]: string[] };
  timestamp: number;
}

interface ProcessedTranslations {
  [key: string]: string;
}

interface CrowdinConfig {
  defaultLanguage?: string;
  fileFormat?: "json" | "crowdin-json";
}

interface CrowdinTranslationResponse {
  [key: string]: CrowdinTranslationItem;
}

interface CrowdinTranslationItem {
  context: string;
  identifier: string;
  labels: string;
  max_length: number | null;
  source_string: string;
  translation: string;
}

@Injectable({
  providedIn: "root",
})
export class CrowdinOtaService {
  private currentLanguage = new BehaviorSubject<string>("en");
  private manifest: CrowdinManifest | null = null;
  private translationsCache = new Map<string, ProcessedTranslations>();

  private config: CrowdinConfig = {
    defaultLanguage: "en",
    fileFormat: "crowdin-json",
  };

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private _storeManagerService: StoreManagerService
  ) {}

  getCurrentLanguage(): Observable<string> {
    return this.currentLanguage.asObservable();
  }

  /**
   * Load manifest from Crowdin
   */
  public async loadManifest(): Promise<CrowdinManifest> {
    if (this.manifest) {
      return this.manifest;
    }

    try {
      const manifestUrl = `${environment.proxyUrl}/businessPortal/accessKeys/admin/manifest`;

      const manifest = await this.http
        .get<CrowdinManifest>(manifestUrl)
        .toPromise();

      if (!manifest) {
        throw new Error("Failed to load Crowdin manifest");
      }

      this.manifest = manifest;
      return this.manifest;
    } catch (error) {
      console.error("Error loading Crowdin manifest:", error);
      throw error;
    }
  }

  /**
   * Get available languages from manifest
   */
  public async getAvailableLanguages(): Promise<string[]> {
    const manifest = await this.loadManifest();
    return manifest.languages;
  }

  /**
   * Check if language is supported
   */
  public async isLanguageSupported(languageCode: string): Promise<boolean> {
    const availableLanguages = await this.getAvailableLanguages();
    return availableLanguages.includes(languageCode);
  }

  /**
   * Get standards compliant locale for a language
   */
  private getStandardsCompliantLocale(
    languageCode: string,
    manifest: CrowdinManifest
  ): string {
    if (manifest.languages && manifest.languages.includes(languageCode)) {
      const langCode = manifest.content[languageCode];
      return langCode[0];
    }
    return languageCode;
  }

  /**
   * Process Crowdin JSON format to simple key-value pairs
   */
  private processCrowdinTranslations(
    crowdinData: CrowdinTranslationResponse
  ): ProcessedTranslations {
    const processed: ProcessedTranslations = {};

    Object.values(crowdinData).forEach((item: CrowdinTranslationItem) => {
      // Use the identifier as the key and translation as the value
      processed[item.identifier] = item.translation;

      // Also support context-based keys if context is provided
      if (item.context && item.context.trim() !== "") {
        processed[item.context] = item.translation;
      }
    });

    return processed;
  }

  /**
   * Load translations for a specific language
   */
  async loadTranslations(languageCode: string): Promise<ProcessedTranslations> {
    // Check cache first
    const cacheKey = `${languageCode}-${this.config.fileFormat}`;
    if (this.translationsCache.has(cacheKey)) {
      return this.translationsCache.get(cacheKey)!;
    }

    try {
      const manifest = await this.loadManifest();

      if (!manifest.languages.includes(languageCode)) {
        throw new Error(`Language "${languageCode}" is not available`);
      }

      const locale = this.getStandardsCompliantLocale(languageCode, manifest);
      const url = `${environment.proxyUrl}/businessPortal/accessKeys/admin/translations?languageId=${locale}`;

      let rawTranslations: any = null;
      let lastError: any = null;

      try {
        console.log(`Attempting to load translations from: ${url}`);
        rawTranslations = await this.http.get(url).toPromise();
      } catch (error) {
        console.warn(`Failed to load from ${url}:`, error);
        lastError = error;
      }

      if (!rawTranslations) {
        throw lastError || new Error("All translation URLs failed");
      }

      let processedTranslations: ProcessedTranslations;

      if (this.config.fileFormat === "crowdin-json") {
        // Handle Crowdin's format with metadata
        processedTranslations = this.processCrowdinTranslations(
          rawTranslations as CrowdinTranslationResponse
        );
      } else {
        // Handle simple key-value JSON format
        processedTranslations = rawTranslations as ProcessedTranslations;
      }

      // Cache the processed translations
      this.translationsCache.set(cacheKey, processedTranslations);

      return processedTranslations;
    } catch (error) {
      console.error(`Error loading translations for ${languageCode}:`, error);
      throw error;
    }
  }

  /**
   * Switch to a specific language
   */
  async useLanguage(languageCode: string): Promise<void> {
    try {
      const isSupported = await this.isLanguageSupported(languageCode);

      if (!isSupported) {
        console.warn(
          `Language "${languageCode}" not supported. Using default language.`
        );
        languageCode = this.config.defaultLanguage || "en";
      }

      const translations = await this.loadTranslations(languageCode);

      this.translate.setTranslation(languageCode, translations, true);
      this.translate.use(languageCode);

      this.currentLanguage.next(languageCode);

      this._storeManagerService.setUserLanguageToStore(languageCode);

      console.log(`Successfully switched to language: ${languageCode}`);
    } catch (error) {
      console.error("Error switching language:", error);

      if (languageCode !== this.config.defaultLanguage) {
        await this.useLanguage(this.config.defaultLanguage || "en");
      }
    }
  }

  /**
   * Initialize with stored language preference
   */
  public async initializeLanguage(): Promise<void> {
    this.handleDefaultTranslationsLoad();

    let storedLanguage =
      this._storeManagerService.getUserSelectedLanguageFromStore();
    storedLanguage = storedLanguage.split("-")[0];
    const browserLanguage = navigator.language.split("-")[0];
    const defaultLanguage =
      storedLanguage || browserLanguage || this.config.defaultLanguage || "en";

    await this.useLanguage(defaultLanguage);
  }

  public handleDefaultTranslationsLoad() {
    const translations = require("./translations.json");
    const processed = this.processCrowdinTranslations(translations);
    this.translate.setTranslation("en", processed, false);
    this.translate.use("en");
  }
}
